import { Grid3X3, List, Target } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { EnhancedProjectCard } from "./project-pipeline";
import { getCompleteProjects, IProject } from "../../project";
import { useAuth } from "../../auth";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import { IDashboardFilterData, Ipagination, ApiResponse } from "../../core/_models";
import { OverlayTrigger, Spinner, Button, Tooltip } from "react-bootstrap";
import { LogError } from "../../../../utils/GlobalLogging";
import SkeletonCard from "../../apps/components/skeleton/SkeletonCard";
import { debounce } from "lodash";
import { getApiErrorMessage } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";
import SectionHeader from "./SectionHeader";
type Props = {
    filters: IDashboardFilterData
}

type projectResponse = ApiResponse<{ projects: IProject[], pagination: Ipagination, total_sectors: number }>

const ProjectPipeine: FC<Props> = ({ filters }) => {
    const { auth } = useAuth();
    const { showSnackbar } = useSnackbar();
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
    const [loading, setLoading] = useState<boolean>(false);
    const [projects, setProjects] = useState<IProject[]>([]);
    const [totalSectors, setTotalSectors] = useState<number>(0);
    const [filter, setFilter] = useState<string>("");

    // Pagination state
    const [page, setPage] = useState<number>(1);
    const ITEMS_PER_PAGE = 12;

    const [totalRecords, setTotalRecords] = useState<number>(0);

    // Refs for performance optimization
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isFirstRender = useRef<boolean>(true);
    const requestIdRef = useRef<number>(0);
    const abortControllerRef = useRef<AbortController | null>(null);

    const handleSearchChange = useCallback(
        debounce((value: string) => {
            setFilter(value);
            setPage(1);
            setProjects([]);
        }, 500),
        []
    );

    useEffect(() => {
        return () => {
            handleSearchChange.cancel();
            abortControllerRef.current?.abort();
        };
    }, [handleSearchChange]);

    useEffect(() => {
        setPage(1);
        setProjects([]);
    }, [filters]);

    const hasMoreProjects = projects.length < totalRecords;
    const loadedProjectCount = projects.length;

    const fetchProjects = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            setLoading(true);
            const projectResponse = await getCompleteProjects(
                filters,
                page,
                ITEMS_PER_PAGE,
                filter,
                true,
                controller.signal
            ) as projectResponse;
            if (requestId !== requestIdRef.current || controller.signal.aborted) {
                return;
            }
            if (projectResponse.status === "ok") {
                const projectData = projectResponse.data?.projects || [];
                if (page === 1) {
                    setProjects(projectData);
                } else {
                    setProjects((prevProjects) => [...prevProjects, ...projectData]);
                }
                setTotalRecords(projectResponse.data?.pagination?.total_records || projectData.length || 0);
                setTotalSectors(projectResponse.data?.total_sectors || 0);
            } else {
                showSnackbar(projectResponse.message, 'danger');
            }
        } catch (error) {
            if (isAbortedError(error) || requestId !== requestIdRef.current) {
                return;
            }
            showSnackbar(getApiErrorMessage(error, "Failed to fetch projects:"), 'danger');
            LogError("Failed to fetch projects", error, "ProjectPipeine");
        } finally {
            if (requestId === requestIdRef.current && !controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [filters, page, ITEMS_PER_PAGE, filter, showSnackbar]);

    // Load project list when auth, filters, page, or searchTerm changes
    useEffect(() => {
        if (!auth?.user_id) return;

        if (page === 1) {
            setProjects([]);
            setLoading(true);
        }

        const timeoutId = setTimeout(() => {
            fetchProjects();
        }, isFirstRender.current ? 100 : 0);

        isFirstRender.current = false;

        return () => {
            clearTimeout(timeoutId);
            abortControllerRef.current?.abort();
        };
    }, [auth?.user_id, fetchProjects, page]);

    const renderLoadMoreButton = useCallback(() => {
        if (!hasMoreProjects) return null;

        return (
            <div className="d-flex flex-column align-items-center mt-4 gap-2">
                <div className="text-gray-700">
                    Showing {loadedProjectCount} of {totalRecords} projects
                </div>
                <Button
                    className="btn-primary"
                    onClick={() => setPage((prev) => prev + 1)}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Loading...
                        </>
                    ) : (
                        'Load more projects'
                    )}
                </Button>
            </div>
        );
    }, [hasMoreProjects, loadedProjectCount, totalRecords, loading]);

    return (
        <div className='position-relative'>
            <SectionHeader
                title="Project Portfolio"
                description={`${totalRecords} projects across ${totalSectors} sectors`}
                icon={Target}
                action={(
                    <div className="d-flex align-items-center flex-column flex-sm-row justify-content-between gap-4 w-100 w-md-auto">
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="form-control form-control-md mw-100 w-100 w-sm-300px w-md-250px w-xl-300px"
                            placeholder="Search"
                            onChange={(e) => handleSearchChange(e.target.value)}
                            aria-label="Search projects"
                        />
                        <div className="rounded-2xl p-1 text-center w-100px">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{"Grid View"} </Tooltip>}
                            >
                                <button
                                    onClick={() => setViewMode("grid")}
                                    className={`p-3 border border-0 rounded-circle ${viewMode === "grid"
                                        ? "bg-primary text-white shadow-apple-sm"
                                        : "text-muted bg-white"
                                        }`}
                                >
                                    <Grid3X3 className="h-4 w-4" />
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{"List View"} </Tooltip>}
                            >
                                <button
                                    onClick={() => setViewMode("list")}
                                    className={`p-3 border border-0 rounded-circle ${viewMode === "list"
                                        ? "bg-primary text-white shadow-apple-sm"
                                        : "text-muted bg-white"
                                        }`}
                                >
                                    <List className="h-4 w-4" />
                                </button>
                            </OverlayTrigger>
                        </div>
                    </div>
                )}
            />

            {/* Project Grid */}
            <div className="row g-4 mb-5">
                {loading && projects.length === 0 ? (
                    Array.from({ length: 9 }).map((_, index) => (
                        <div key={index} className={`col-12 ${viewMode === "list"
                            ? "col-md-12 col-lg-12"
                            : "col-md-6 col-xl-4"
                            }`}>
                            <SkeletonCard
                                wrapperClassName=""
                                className="mb-4"
                                titleWidth="75%"
                                valueWidth="75%"
                                showIcon={false}
                                lines={7}
                            />
                        </div>
                    ))
                ) : (
                    projects.map((project: IProject) => {
                        return <div
                            className={`col-12 ${viewMode === "list"
                                ? "col-md-12 col-lg-12"
                                : "col-md-6 col-xl-4"
                                }`}
                            key={project.project_id}
                        >
                            <EnhancedProjectCard project={project} />
                        </div>
                    })
                )}
            </div>
            {renderLoadMoreButton()}
        </div>
    );
};

export { ProjectPipeine };